'use client';

import { AppShell, Box } from '@mantine/core';
import { useEffect, useState } from 'react';

import { Footer } from '~/components/shared/Footer/Footer';
import { AppShellHeaderRoot } from '~/domains/common/components/AppShellHeaderRoot/AppShellHeaderRoot';
import { useIsBiggerThanDesktop } from '~/domains/common/hooks/mediaQueries';
import AppBanners from '~/domains/common/components/AppBanners/AppBanners';
import Debug from '~/domains/debug';
import isWebView from '~/utils/isWebView';

import { WebViewLayoutProvider } from './WebViewLayoutContext';

type PageLayoutProps = {
  children: React.ReactNode;
  hasOwnAppshell?: boolean;
  hasAppBanners?: boolean;
  hideFooter?: boolean;
  hideHeader?: boolean;
  hideHeaderOnMobile?: boolean;
  hideChromeInWebView?: boolean;
  /** Server-resolved webview state (getWebViewServerSideProps) so the first render already hides
   *  chrome without a flash. isWebView() covers client-side navigations after mount. */
  isWebViewInitial?: boolean;
};

export function PageLayout({
  children,
  hasOwnAppshell,
  hasAppBanners,
  hideFooter,
  hideHeader,
  hideHeaderOnMobile,
  hideChromeInWebView,
  isWebViewInitial,
}: PageLayoutProps) {
  const isDesktopUp = useIsBiggerThanDesktop();
  // The server resolves isWebViewInitial from the request, so the first render (server and hydration)
  // already hides chrome in a webview with no flash. isWebView() reads window (search param / cookie)
  // and is client-only, so it's gated on `mounted` to catch client-side navigations after hydration.
  const [mounted, setMounted] = useState(false);
  // Only an opt-in page reads `mounted` (the webViewChromeHidden `&&` below short-circuits when
  // hideChromeInWebView is false), so gate the effect on it — otherwise this setState makes every
  // page in the app take one extra render on mount.
  useEffect(() => {
    if (hideChromeInWebView) setMounted(true);
  }, [hideChromeInWebView]);
  const webViewChromeHidden =
    Boolean(hideChromeInWebView) && (Boolean(isWebViewInitial) || (mounted && isWebView()));
  const isHeaderHidden = hideHeader || (hideHeaderOnMobile && !isDesktopUp) || webViewChromeHidden;

  return hasOwnAppshell ? (
    <WebViewLayoutProvider value={webViewChromeHidden}>{children}</WebViewLayoutProvider>
  ) : (
    <WebViewLayoutProvider value={webViewChromeHidden}>
      <AppShell
        padding={0}
        header={isHeaderHidden ? undefined : <AppShellHeaderRoot />}
        styles={{
          root: isHeaderHidden ? { ['--mantine-header-height' as string]: '0px' } : undefined,
          main: {
            display: 'flex',
            flexFlow: 'column nowrap',
            padding: 0,
            minHeight: 'calc(100vh - 122px)',
            width: '100%',
          },
        }}
      >
        {hasAppBanners && !webViewChromeHidden && <AppBanners />}
        <Box h="100%">{children}</Box>
      </AppShell>
      {!hideFooter && !webViewChromeHidden ? <Footer /> : null}
      <Debug />
    </WebViewLayoutProvider>
  );
}
